import { extractErrorMessage, calculateNextPage, ApplicationStatus } from './pendingApplicationsUtil'

/**
 * Parameters for createApplicationActionHandler
 */
export interface CreateApplicationActionHandlerParams {
  row: any
  status: ApplicationStatus
  savedNotes: Record<string, string>
  applications: any[]
  currentPage: number
  perPage: number
  dispatch: any
  updateApplicationStatus: any
  removePendingApplication: any
  fetchPendingApplications: any
  fetchPastApplications: any
  onClearNote: (appId: string) => void
  onSetActionLoadingId: (id: string | null) => void
}

/**
 * Factory function to create approve/deny handlers
 * Eliminates code duplication between handleApprove and handleDeny
 * 
 * This function encapsulates the entire flow:
 * 1. Set loading state
 * 2. Prepare payload with status and comments
 * 3. Dispatch update action
 * 4. Handle success: clear note, remove from pending, fetch updated lists
 * 5. Handle error: show error message
 * 6. Finally: clear loading state
 * 
 * @param params - Handler configuration parameters
 * @returns Async function to execute the action
 */
export const createApplicationActionHandler = ({
  row,
  status,
  savedNotes,
  applications,
  currentPage,
  perPage,
  dispatch,
  updateApplicationStatus,
  removePendingApplication,
  fetchPendingApplications,
  fetchPastApplications,
  onClearNote,
  onSetActionLoadingId,
}: CreateApplicationActionHandlerParams) => {
  return async () => {
    try {
      const appIdStr = String(row?.id)
      onSetActionLoadingId(appIdStr)
      const savedComment = savedNotes[appIdStr] || ''

      // Build payload with status and optional comments
      const payload: any = {
        status,
        applicationId: appIdStr,
      }

      if (savedComment) {
        payload.comments = savedComment
      }

      // Dispatch update action with empNuid
      const result = await dispatch(
        updateApplicationStatus({ ...payload, empNuid: row?.empNuid })
      )

      // Handle success response
      if (updateApplicationStatus.fulfilled.match(result)) {
        // Clear the saved note after successful submission
        onClearNote(appIdStr)

        // Immediately remove the application from Redux state
        dispatch(removePendingApplication(appIdStr))

        // Calculate next page (reset to 1 if last application on page)
        const newPage = calculateNextPage(applications.length, currentPage)

        // Fetch updated lists to sync with server
        await dispatch(fetchPendingApplications({ page: newPage, per_page: perPage }))
        await dispatch(fetchPastApplications({ page: 1, per_page: perPage }))
      }
      // Handle rejected response
      else if (updateApplicationStatus.rejected.match(result)) {
        const actionText = status === 'Manager Accepted' ? 'approving' : 'denying'
        console.error(`Error ${actionText} application:`, result.payload)

        const fallbackMessage = `An error occurred while ${actionText} the application.`
        const errorMessage = extractErrorMessage(result.payload, fallbackMessage)
        alert(errorMessage)
      }
    } catch (err) {
      const actionText = status === 'Manager Accepted' ? 'approving' : 'denying'
      console.error(`Unexpected error ${actionText} application:`, err)
      alert(`An unexpected error occurred while ${actionText} the application.`)
    } finally {
      onSetActionLoadingId(null)
    }
  }
}

/**
 * Validates if application row has required fields
 * @param row - Application row data
 * @returns True if row has id and empNuid
 */
export const isValidApplicationRow = (row: any): boolean => {
  return !!row?.id && !!row?.empNuid
}

/**
 * Validates if note text is within character limit
 * @param noteText - Note text to validate
 * @param maxLength - Maximum allowed length
 * @returns True if within limit
 */
export const isValidNoteLength = (noteText: string, maxLength: number): boolean => {
  return noteText.length <= maxLength
}